import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Crown, TrendingUp, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * InShiftSoftSell
 *
 * Shown on the final confirmation step of shift creation for pay-per-job
 * clients. Takes the client's current-month post count, adds 1 (this shift
 * they're about to post), multiplies by pay-per-job price, subtracts Pro
 * monthly — and if that's positive, shows the savings line.
 *
 * Deliberately quiet styling (inline, single line) so it never blocks the
 * form flow. Subtle CTA button navigates to the subscription page with
 * `cta=shift_create` context preserved so the subscription page can
 * highlight the relevant plan on landing.
 *
 * Returns null for Pro/Enterprise users — they've already converted.
 */

const InShiftSoftSell = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statusRes, plansRes] = await Promise.all([
          api.get('/monetization/client-subscriptions/status').catch(() => ({ data: {} })),
          api.get('/monetization/client-subscriptions/plans').catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const status = statusRes.data || {};
        const plans = plansRes.data || {};
        const plan = (status.plan_id || 'pay_per_job').toLowerCase();
        if (plan !== 'pay_per_job') {
          setData(null);
          return;
        }
        const proPlan = (plans.plans || []).find((p) => p.id === 'pro');
        const payPerJobPrice = plans.pay_per_job?.price ?? 10.95;
        const jobs = Math.max(0, status.jobs_this_month || 0);
        // Include the shift they're about to post in the projection.
        const wouldBeCount = jobs + 1;
        const payPerJobTotal = wouldBeCount * payPerJobPrice;
        const proMonthly = proPlan?.price_monthly || 199;
        const savings = Math.max(0, payPerJobTotal - proMonthly);
        setData({
          jobs,
          wouldBeCount,
          payPerJobPrice,
          proMonthly,
          savings,
          show: savings >= 20, // don't bother if the delta is tiny
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data || !data.show) return null;

  return (
    <div
      className="w-full mb-4 px-4 py-3 rounded-lg border border-cyan-200 bg-gradient-to-r from-cyan-50 to-white flex flex-wrap items-center justify-between gap-3"
      data-testid="shift-create-soft-sell"
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="bg-cyan-500/15 border border-cyan-300/60 rounded-md p-1.5 flex-shrink-0">
          <Crown className="w-4 h-4 text-cyan-700" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-slate-800 leading-tight">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600 inline mr-1 -mt-0.5" />
            You'd save{' '}
            <strong className="text-cyan-700">${data.savings.toFixed(0)}</strong> this month with Pro.
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
            At {data.wouldBeCount} posts × ${data.payPerJobPrice.toFixed(2)} = ${(data.wouldBeCount * data.payPerJobPrice).toFixed(0)} — Pro is flat ${data.proMonthly}/mo.
          </p>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="border-cyan-400 text-cyan-700 hover:bg-cyan-50 flex-shrink-0"
        onClick={() => navigate('/client/settings/subscription?plan=pro&cta=shift_create')}
        data-testid="shift-create-soft-sell-upgrade"
      >
        Upgrade to Pro <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
  );
};

export default InShiftSoftSell;
